import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import APIClient, { endpoints } from '@shared/api';
import { API_URL_CTRACKER } from '@shared/api/config';

const APIClientCTracker = axios.create({
  baseURL: `${API_URL_CTRACKER}/api`,
  headers: {
    Accept: 'application/json',
  },
  timeout: 120000,
});

APIClientCTracker.interceptors.request.use((config) => {
  return config;
});

/**
 * Pide a ConnectServices (CS NETCORE) un token fresco para Claims Tracker API,
 * forzando la invalidación del token cacheado del servidor (forceRefresh=true).
 * Se usa para recuperarse de un 401 causado por un token que CT API ya no acepta.
 */
async function requestFreshCTrackerToken(): Promise<string> {
  const result = await APIClient.post(
    `${endpoints.POST_CLAIMS_TRACKER_TOKEN}?forceRefresh=true`,
  );

  return result.data.data;
}

/**
 * Decodifica (sin validar firma) los claims diagnósticos del payload de un JWT
 * para dejar rastro del ORIGEN del token ante un 401: iss, aud, exp, jti. Nunca
 * registra la firma ni el token completo. Permite identificar, si el incidente
 * vuelve, de qué emisor/entorno vino el token que CT API rechazó.
 */
function describeTokenFromAuthHeader(
  authHeader: unknown,
): Record<string, string> {
  if (typeof authHeader !== 'string' || !authHeader.startsWith('Bearer ')) {
    return { token: '<no-bearer>' };
  }

  const token = authHeader.slice('Bearer '.length);
  const segments = token.split('.');
  if (segments.length !== 3) {
    return { token: '<malformed>' };
  }

  try {
    const payload = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(payload)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    const claims = JSON.parse(json) as Record<string, unknown>;

    return {
      iss: String(claims.iss ?? '<none>'),
      aud: String(claims.aud ?? '<none>'),
      exp: claims.exp
        ? new Date(Number(claims.exp) * 1000).toISOString()
        : '<none>',
      jti: String(claims.jti ?? '<none>'),
      prefix: token.slice(0, 10),
    };
  } catch {
    return { token: '<unparseable-payload>' };
  }
}

/**
 * Enmascara identificadores de una ruta para dejar rastro del endpoint afectado
 * sin registrar datos sensibles (UId de claim, IDs numéricos). Sustituye GUIDs y
 * segmentos numéricos por ':id', y descarta el query string por completo.
 */
function sanitizePath(url: unknown): string {
  if (typeof url !== 'string' || url.length === 0) {
    return '<none>';
  }

  const path = url.split('?')[0];

  return path
    .split('/')
    .map((segment) => {
      const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        segment,
      );
      const isNumericId = /^\d+$/.test(segment);

      return isGuid || isNumericId ? ':id' : segment;
    })
    .join('/');
}

APIClientCTracker.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retriedWithFreshToken?: boolean })
      | undefined;

    // Ante un 401 de Claims Tracker API, el token entregado ya no es aceptado
    // (p.ej. firmado con otra clave tras un deploy/cambio de entorno). Pedimos un
    // token fresco forzando la invalidación de cache en ConnectServices y
    // reintentamos la request original UNA sola vez para evitar bucles.
    if (error.response?.status === 401 && originalRequest) {
      // Diagnóstico del token rechazado: deja en consola/Sentry el origen (iss/aud/exp/jti)
      // del token que CT API rechazó con 401, para identificar la causa si reincide.
      console.error('Claims Tracker 401 — token rechazado:', {
        // URL sin identificadores: enmascaramos GUIDs/IDs numéricos de la ruta
        // para no registrar UId de claim u otros datos sensibles.
        path: sanitizePath(originalRequest.url),
        method: originalRequest.method,
        token: describeTokenFromAuthHeader(
          originalRequest.headers?.Authorization,
        ),
        alreadyRetried: Boolean(originalRequest._retriedWithFreshToken),
      });
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retriedWithFreshToken
    ) {
      originalRequest._retriedWithFreshToken = true;

      try {
        const freshToken = await requestFreshCTrackerToken();

        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${freshToken}`;

        return await APIClientCTracker(originalRequest);
      } catch (retryError) {
        // Registrar solo campos seguros. No volcar el error completo: su `config`
        // (incluido en AxiosError.toJSON de axios 1.x) lleva el header Authorization
        // con el token Bearer fresco y se filtraría a consola/Sentry.
        const retryAxiosError = retryError as AxiosError;
        console.error('Claims Tracker 401 retry with fresh token failed:', {
          status: retryAxiosError.response?.status,
          code: retryAxiosError.code,
          method: retryAxiosError.config?.method,
        });
        throw retryError;
      }
    }

    // No volcar el error completo: AxiosError.toJSON (axios 1.x) incluye `config`
    // con el header Authorization. Registrar solo campos seguros.
    console.error('Claims Tracker request error:', {
      status: error.response?.status,
      code: error.code,
      method: error.config?.method,
      path: sanitizePath(error.config?.url),
    });

    throw error;
  },
);

export default APIClientCTracker;
